import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { ChevronIcon } from "../chevronIcon";
import { getMonthsNames } from "../utils";
import { DateType } from "../types";
import { Props } from "./types";
import { getWeek, getDaysTexts } from "./utils";
import styles from "./styles.scss";

function Days({
  date,
  cursor,
  editCursor,
  editDate,
  switchViews,
  ...props
}: Props) {
  const { t } = useTranslation();
  const months = getMonthsNames(t);
  const daysNames = getDaysTexts(t);
  const minDate = props.minDate
    ? new Date(
        Date.UTC(props.minDate.year, props.minDate.month, props.minDate.day),
      )
    : new Date(0);
  const maxDate = props.maxDate
    ? new Date(
        Date.UTC(props.maxDate.year, props.maxDate.month, props.maxDate.day),
      )
    : new Date(99999, 1);
  const isDateAllowed = (date: DateType) => {
    const d = new Date(Date.UTC(date.year, date.month, date.day));

    const isAboveMinDate = minDate
      ? d > minDate ||
        (d.getUTCFullYear() === minDate.getUTCFullYear() &&
          d.getUTCMonth() === minDate.getUTCMonth() &&
          d.getUTCDate() >= minDate.getUTCDate())
      : true;

    const isBelowMaxDate = maxDate ? d <= maxDate : true;

    return isAboveMinDate && isBelowMaxDate;
  };

  return (
    <div className={styles.calendar}>
      <div className={styles.heading}>
        <div
          className={classNames(styles.iconBox, {
            [styles.disabledIconBox]:
              new Date(
                Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth()),
              ) <= minDate,
          })}
          onClick={(e) => {
            e.stopPropagation();
            editCursor({ month: cursor.getUTCMonth() - 1, day: 10 });
          }}
        >
          <ChevronIcon className={classNames(styles.icon, styles.leftIcon)} />
        </div>
        <h3 className={styles.title} onClick={() => switchViews("months")}>
          {months[cursor.getUTCMonth()]} {cursor.getUTCFullYear()}
        </h3>
        <div
          className={classNames(styles.iconBox, {
            [styles.disabledIconBox]:
              new Date(
                Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth()),
              ) > maxDate,
          })}
          onClick={(e) => {
            e.stopPropagation();
            editCursor({ month: cursor.getUTCMonth() + 1, day: 10 });
          }}
        >
          <ChevronIcon className={classNames(styles.icon, styles.rightIcon)} />
        </div>
      </div>
      <ul className={styles.week}>
        {getWeek({
          year: cursor.getUTCFullYear(),
          month: cursor.getUTCMonth(),
          day: cursor.getUTCDate(),
        }).map((days, index) => (
          <li key={`${daysNames[index]}__${index}`} className={styles.weekItem}>
            <h3 className={styles.dayName}>{daysNames[index]}</h3>
            <ul className={styles.dayList}>
              {days.map((day, index) => {
                const isPlaceholder = !parseInt(day, 10);
                const isAllowed = isDateAllowed({
                  year: cursor.getUTCFullYear(),
                  month: cursor.getUTCMonth(),
                  day: parseInt(day, 10),
                });
                const className = classNames(styles.day, {
                  [styles.selectedDay]:
                    date.day === parseInt(day) &&
                    date.month === cursor.getUTCMonth() &&
                    date.year === cursor.getUTCFullYear(),
                  [styles.disabledDay]: !isAllowed,
                });

                return (
                  <li
                    key={`${day}__${index}`}
                    style={{ cursor: parseInt(day) ? "cursor" : "initial" }}
                    className={className}
                    onClick={
                      !isPlaceholder && isAllowed
                        ? (e) => {
                            e.stopPropagation();
                            editDate({ day: parseInt(day, 10) });
                          }
                        : undefined
                    }
                  >
                    {day}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
      {!!props.clear && !!props.today ? (
        <div className={styles.footer}>
          <div className={styles.clear} onClick={props.today}>
            <div className={styles.button}>
              <p>{t("calendar.today")}</p>
            </div>
          </div>
          <div className={styles.clear} onClick={props.clear}>
            <div className={styles.button}>
              <p>{t("calendar.clear")}</p>
            </div>
          </div>
        </div>
      ) : !!props.clear ? (
        <div className={styles.clear} onClick={props.clear}>
          <div className={styles.button}>
            <p>{t("calendar.clear")}</p>
          </div>
        </div>
      ) : (
        !!props.today && (
          <div className={styles.clear} onClick={props.today}>
            <div className={styles.button}>
              <p>{t("calendar.today")}</p>
            </div>
          </div>
        )
      )}
    </div>
  );
}

export { Days };
